import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login, googleLogin } from "../../actions";
import toast from "react-hot-toast";
import { Capacitor } from "@capacitor/core";
import { GoogleAuth } from "@codetrix-studio/capacitor-google-auth";

// The shell has no dashboard routes of its own — after login it hands off to
// the matching micro-frontend's own deployed domain (see the comment atop
// App.jsx). A relative navigate("/admin") would just re-hit the shell's own
// catch-all route (back to this login screen), so this is a real cross-origin
// redirect instead.
const roleUrl = {
  admin: process.env.REACT_APP_ADMIN_URL,
  teacher: process.env.REACT_APP_STAFF_URL,
  staff: process.env.REACT_APP_STAFF_URL,
  student: process.env.REACT_APP_STUDENT_URL,
  parent: process.env.REACT_APP_PARENT_URL,
};

const ROLES = [
  { label: "Admin", icon: "🛡️", color: "#6366F1", bg: "#EEF2FF" },
  { label: "Teacher", icon: "👩‍🏫", color: "#16A34A", bg: "#F0FDF4" },
  { label: "Student", icon: "🎓", color: "#D97706", bg: "#FFFBEB" },
  { label: "Parent", icon: "👨‍👩‍👧", color: "#E11D48", bg: "#FFF1F2" },
];

const FEATURES = [
  { icon: "📊", text: "Real-time attendance & marks tracking" },
  { icon: "💰", text: "Fee collection & salary management" },
  { icon: "📚", text: "Library, hostel & transport modules" },
  { icon: "📢", text: "Notices & homework management" },
];

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, isAuthenticated, user, error } = useSelector((s) => s.auth);
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [showPw, setShowPw] = useState(false);
  const googleBtnRef = useRef(null);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch({ type: "CLEAR_ERRORS" });
    }
    if (isAuthenticated && user) {
      const target = roleUrl[user.role];
      const token = localStorage.getItem("token");
      if (target && token) {
        window.location.href = `${target}/?token=${encodeURIComponent(token)}`;
      } else if (target) {
        window.location.href = target;
      } else {
        navigate("/login");
      }
    }
  }, [error, isAuthenticated, user, navigate, dispatch]);

  // Loads Google's Identity Services script once, then renders the
  // official "Sign in with Google" button into googleBtnRef. On success,
  // Google calls back with a credential (idToken) which we exchange via
  // the googleLogin thunk — same LOGIN_SUCCESS flow as normal login.
  //
  // Skipped entirely inside Electron — window.electronAPI.googleSignIn
  // (added via preload.ts) handles that flow instead, using the system
  // browser + a local redirect server (see the button below).
  const isElectron =
    typeof window !== "undefined" && !!window.electronAPI?.googleSignIn;

  // Native Android/iOS app (Capacitor) check — on native, the web GSI
  // button below gets blocked inside the WebView (disallowed_useragent),
  // so we use the native GoogleAuth plugin instead, same as Electron does
  // with the system browser.
  const isNative = Capacitor.isNativePlatform();

  const handleNativeGoogleSignIn = async () => {
    try {
      const user = await GoogleAuth.signIn();
      dispatch(googleLogin(user.authentication.idToken));
    } catch (err) {
      toast.error(err?.message || "Google sign-in failed or was cancelled");
    }
  };

  useEffect(() => {
    if (isElectron || isNative) return;

    const initializeGoogleButton = () => {
      if (!window.google || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: (response) => {
          dispatch(googleLogin(response.credential));
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        width: 364,
        text: "signin_with",
      });
    };

    if (window.google) {
      initializeGoogleButton();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = initializeGoogleButton;
    document.body.appendChild(script);
  }, [dispatch, isElectron, isNative]);

  // Electron path: ask the main process to run the system-browser OAuth
  // flow; it resolves with a Google idToken we feed into the same
  // googleLogin thunk the website uses.
  const handleElectronGoogleSignIn = async () => {
    try {
      const idToken = await window.electronAPI.googleSignIn();
      dispatch(googleLogin(idToken));
    } catch (err) {
      // TEMP DEBUG: exact reason chuppinchadam kosam. Fix aina tarvatha
      // dobbesi malli generic message pettochu.
      toast.error(err?.message || "Google sign-in failed or was cancelled");
    }
  };

  const handle = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.identifier || !form.password)
      return toast.error("Please fill all fields");
    const isEmail = form.identifier.includes("@");
    dispatch(
      login(
        isEmail ? form.identifier : undefined,
        !isEmail ? form.identifier : undefined,
        form.password,
      ),
    );
  };

  return (
    <div
      style={{
        display: "flex",
        minHeight: "100vh",
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        background: "#F8FAFC",
      }}
    >
      {/* Responsive fix: on narrow (mobile) screens, hide the decorative
          left panel entirely and let the right (form) panel take full
          width instead of its fixed 460px — that fixed width was pushing
          content off-screen on phones. */}
      <style>{`
        @media (max-width: 768px) {
          .login-left-panel { display: none !important; }
          .login-right-panel {
            width: 100% !important;
            padding: 32px 24px !important;
          }
        }
      `}</style>
      {/* ── Left Panel ── */}
      <div
        className="login-left-panel"
        style={{
          flex: 1,
          background:
            "linear-gradient(160deg, #0F172A 0%, #1E293B 50%, #1a237e 100%)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "60px 56px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* decorative circles */}
        <div
          style={{
            position: "absolute",
            top: -80,
            right: -80,
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: "rgba(99,102,241,0.12)",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -60,
            left: -60,
            width: 240,
            height: 240,
            borderRadius: "50%",
            background: "rgba(99,102,241,0.08)",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          <div style={{ fontSize: "2.8rem", marginBottom: 8 }}>🎓</div>
          <h1
            style={{
              fontSize: "2rem",
              fontWeight: 800,
              color: "#fff",
              letterSpacing: "-0.5px",
              marginBottom: 8,
            }}
          >
            Ochrel School
          </h1>
          <p
            style={{
              fontSize: "1rem",
              color: "rgba(255,255,255,0.5)",
              marginBottom: 48,
              lineHeight: 1.5,
            }}
          >
            Empowering Education,
            <br />
            Inspiring Futures
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {FEATURES.map((f, i) => (
              <div
                key={i}
                style={{ display: "flex", alignItems: "center", gap: 14 }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    background: "rgba(99,102,241,0.2)",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.1rem",
                    flexShrink: 0,
                  }}
                >
                  {f.icon}
                </div>
                <span
                  style={{
                    fontSize: "0.88rem",
                    color: "rgba(255,255,255,0.75)",
                    lineHeight: 1.4,
                  }}
                >
                  {f.text}
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 56,
              padding: "16px 20px",
              background: "rgba(255,255,255,0.05)",
              borderRadius: 14,
              border: "1px solid rgba(255,255,255,0.08)",
            }}
          >
            <p
              style={{
                fontSize: "0.75rem",
                color: "rgba(255,255,255,0.4)",
                marginBottom: 10,
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              LOGIN AS
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {ROLES.map((r) => (
                <div
                  key={r.label}
                  style={{
                    padding: "6px 14px",
                    borderRadius: 8,
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontSize: "0.8rem",
                    color: "rgba(255,255,255,0.7)",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span>{r.icon}</span> {r.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Right Panel ── */}
      <div
        className="login-right-panel"
        style={{
          width: 460,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 48px",
          background: "#fff",
        }}
      >
        <div style={{ width: "100%" }}>
          {/* Header */}
          <div style={{ marginBottom: 36 }}>
            <h2
              style={{
                fontSize: "1.7rem",
                fontWeight: 800,
                color: "#0F172A",
                letterSpacing: "-0.5px",
                marginBottom: 6,
              }}
            >
              Welcome back 👋
            </h2>
            <p style={{ fontSize: "0.875rem", color: "#94A3B8" }}>
              Sign in to your school portal account
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            {/* Identifier */}
            <div style={{ marginBottom: 18 }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: 7,
                }}
              >
                Email or Phone Number
              </label>
              <input
                name="identifier"
                type="text"
                value={form.identifier}
                onChange={handle}
                placeholder="email@school.com or 9999999999"
                autoComplete="username"
                required
                style={{
                  width: "100%",
                  padding: "12px 16px",
                  border: "1.5px solid #E2E8F0",
                  borderRadius: 12,
                  fontSize: "0.9rem",
                  outline: "none",
                  color: "#0F172A",
                  background: "#F8FAFC",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#6366F1";
                  e.target.style.background = "#fff";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#E2E8F0";
                  e.target.style.background = "#F8FAFC";
                }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 28 }}>
              <label
                style={{
                  display: "block",
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  color: "#374151",
                  marginBottom: 7,
                }}
              >
                Password
              </label>
              <div style={{ position: "relative" }}>
                <input
                  name="password"
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={handle}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  style={{
                    width: "100%",
                    padding: "12px 48px 12px 16px",
                    border: "1.5px solid #E2E8F0",
                    borderRadius: 12,
                    fontSize: "0.9rem",
                    outline: "none",
                    color: "#0F172A",
                    background: "#F8FAFC",
                    boxSizing: "border-box",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#6366F1";
                    e.target.style.background = "#fff";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#E2E8F0";
                    e.target.style.background = "#F8FAFC";
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  style={{
                    position: "absolute",
                    right: 14,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "1rem",
                    color: "#94A3B8",
                  }}
                >
                  {showPw ? "🙈" : "👁️"}
                </button>
              </div>
              <div style={{ textAlign: "right", marginTop: 8 }}>
                <span
                  onClick={() => navigate("/forgot-password")}
                  style={{
                    fontSize: "0.8rem",
                    color: "#6366F1",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Forgot password?
                </span>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%",
                padding: "13px",
                background: loading
                  ? "#A5B4FC"
                  : "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                fontSize: "0.95rem",
                fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer",
                letterSpacing: "0.2px",
                transition: "opacity 0.2s",
              }}
            >
              {loading ? "Signing in..." : "Sign in →"}
            </button>
          </form>

          {/* Divider */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              margin: "24px 0",
            }}
          >
            <div style={{ flex: 1, height: 1, background: "#E2E8F0" }} />
            <span style={{ fontSize: "0.75rem", color: "#94A3B8" }}>OR</span>
            <div style={{ flex: 1, height: 1, background: "#E2E8F0" }} />
          </div>

          {/* Google Sign-In button — website renders Google's official
              button into this div; Electron shows a matching custom
              button that triggers the system-browser OAuth flow instead;
              native Android/iOS (Capacitor) shows the same style button
              but triggers the native GoogleAuth plugin flow instead. */}
          {isElectron ? (
            <button
              type="button"
              onClick={handleElectronGoogleSignIn}
              style={{
                width: "100%",
                padding: "11px",
                background: "#fff",
                border: "1.5px solid #E2E8F0",
                borderRadius: 12,
                fontSize: "0.9rem",
                fontWeight: 600,
                color: "#374151",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <span style={{ fontSize: "1.1rem" }}>G</span> Sign in with Google
            </button>
          ) : isNative ? (
            <button
              type="button"
              onClick={handleNativeGoogleSignIn}
              style={{
                width: "100%",
                padding: "11px",
                background: "#fff",
                border: "1.5px solid #E2E8F0",
                borderRadius: 12,
                fontSize: "0.9rem",
                fontWeight: 600,
                color: "#374151",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <span style={{ fontSize: "1.1rem" }}>G</span> Sign in with Google
            </button>
          ) : (
            <div
              ref={googleBtnRef}
              style={{ display: "flex", justifyContent: "center" }}
            />
          )}

          {/* Footer note */}
          <p
            style={{
              textAlign: "center",
              fontSize: "0.75rem",
              color: "#CBD5E1",
              marginTop: 32,
            }}
          >
            Ochrel School Management System © 2024
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
